import React from 'react';
import { CheckCircle2, ArrowRight, CreditCard, Shield, Zap } from 'lucide-react';
import { motion } from 'framer-motion';
export function BrixleyPromo() {
  return <section className="py-24 bg-charcoal-900 text-white relative overflow-hidden" data-id="element-22">
      {/* Background Accents */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none" data-id="element-23">
        <div className="absolute top-[-20%] right-[-10%] w-[70%] h-[70%] rounded-full bg-brand-900/20 blur-[120px]" data-id="element-24" />
        <div className="absolute bottom-[-20%] left-[-10%] w-[50%] h-[50%] rounded-full bg-blue-900/20 blur-[100px]" data-id="element-25" />
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10" data-id="element-26">
        <div className="grid lg:grid-cols-2 gap-16 items-center" data-id="element-27">
          {/* Content */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} data-id="element-28">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 text-sm font-medium mb-6" data-id="element-29">
              <span className="w-2 h-2 rounded-full bg-brand-400 animate-pulse" data-id="element-30" />
              New Platform
            </div>
            <h2 className="text-4xl md:text-5xl font-serif font-semibold mb-6 leading-tight" data-id="element-31">
              Introducing{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-300 to-brand-500" data-id="element-32">
                BrixleyXchange
              </span>
            </h2>
            <p className="text-gray-300 text-lg mb-8 leading-relaxed" data-id="element-33">
              A fintech platform built exclusively for business aviation. Make
              paying for travel and managing spend dramatically easier with a
              prepaid Mastercard® and an FX payments hub designed for
              high-dollar aviation use.
            </p>

            <div className="space-y-4 mb-10" data-id="element-34">
              {['Transactions flow in automatically', 'Capture receipts and invoices on the go', 'Advanced approvals and permissions', 'Real-time visibility instead of end-of-month surprises'].map((feature, i) => <div key={i} className="flex items-start gap-3" data-id="element-35">
                  <CheckCircle2 className="w-6 h-6 text-brand-400 shrink-0" data-id="element-36" />
                  <span className="text-gray-200" data-id="element-37">{feature}</span>
                </div>)}
            </div>

            <div className="flex flex-col sm:flex-row gap-4" data-id="element-38">
              <button className="bg-brand-600 hover:bg-brand-500 text-white px-8 py-4 rounded-xl font-medium transition-all flex items-center justify-center gap-2 group" data-id="element-39">
                Create An Account
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" data-id="element-40" />
              </button>
              <button className="bg-white/10 hover:bg-white/20 text-white border border-white/20 px-8 py-4 rounded-xl font-medium transition-all flex items-center justify-center" data-id="element-41">
                See How It Works
              </button>
            </div>
          </motion.div>

          {/* Visual / Card */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.8
        }} className="relative perspective-1000" data-id="element-42">
            {/* Floating Elements */}
            <motion.div animate={{
            y: [-10, 10, -10]
          }} transition={{
            duration: 6,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute -top-8 -right-8 bg-white/10 backdrop-blur-md border border-white/20 p-4 rounded-2xl shadow-xl z-20 flex items-center gap-4" data-id="element-43">
              <div className="w-12 h-12 rounded-full bg-brand-500/20 flex items-center justify-center" data-id="element-44">
                <Zap className="w-6 h-6 text-brand-400" data-id="element-45" />
              </div>
              <div data-id="element-46">
                <p className="text-sm text-gray-400" data-id="element-47">Instant Transfer</p>
                <p className="font-semibold" data-id="element-48">$45,000.00</p>
              </div>
            </motion.div>

            <motion.div animate={{
            y: [10, -10, 10]
          }} transition={{
            duration: 5,
            repeat: Infinity,
            ease: 'easeInOut',
            delay: 1
          }} className="absolute -bottom-8 -left-8 bg-white/10 backdrop-blur-md border border-white/20 p-4 rounded-2xl shadow-xl z-20 flex items-center gap-4" data-id="element-49">
              <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center" data-id="element-50">
                <Shield className="w-6 h-6 text-emerald-400" data-id="element-51" />
              </div>
              <div data-id="element-52">
                <p className="text-sm text-gray-400" data-id="element-53">Secure Payment</p>
                <p className="font-semibold" data-id="element-54">Verified</p>
              </div>
            </motion.div>

            {/* The Card */}
            <div className="relative w-full max-w-md mx-auto aspect-[1.586/1] rounded-2xl p-8 overflow-hidden shadow-2xl shadow-black/50 border border-white/10 group transform transition-transform hover:scale-105 duration-500" data-id="element-55">
              {/* Card Background */}
              <div className="absolute inset-0 bg-gradient-to-br from-charcoal-800 via-charcoal-900 to-black z-0" data-id="element-56" />
              <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/brushed-alum.png')] opacity-20 mix-blend-overlay z-0" data-id="element-57" />

              {/* Card Content */}
              <div className="relative z-10 h-full flex flex-col justify-between" data-id="element-58">
                <div className="flex justify-between items-start" data-id="element-59">
                  <div className="tracking-[0.2em] text-sm text-gray-300 font-medium" data-id="element-60">
                    BRIXLEYXCHANGE
                  </div>
                  <CreditCard className="w-8 h-8 text-gray-400 opacity-50" data-id="element-61" />
                </div>

                {/* Crane Logo Abstract */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-32 opacity-20" data-id="element-62">
                  <svg viewBox="0 0 100 100" className="w-full h-full fill-white" data-id="element-63">
                    <path d="M50 10 L90 40 L50 90 L10 40 Z" data-id="element-64" />
                    <path d="M50 10 L50 90" stroke="white" strokeWidth="2" data-id="element-65" />
                    <path d="M10 40 L90 40" stroke="white" strokeWidth="2" data-id="element-66" />
                  </svg>
                </div>

                <div data-id="element-67">
                  <div className="w-12 h-9 bg-gradient-to-br from-gray-300 to-gray-400 rounded mb-6 opacity-80" data-id="element-68" />
                  <div className="flex justify-between items-end" data-id="element-69">
                    <div data-id="element-70">
                      <p className="text-xs text-gray-400 mb-1" data-id="element-71">
                        CARDHOLDER NAME
                      </p>
                      <p className="font-mono text-lg tracking-widest text-gray-200" data-id="element-72">
                        ALEXANDER WRIGHT
                      </p>
                    </div>
                    <div className="flex -space-x-3" data-id="element-73">
                      <div className="w-8 h-8 rounded-full bg-red-500/80 mix-blend-screen" data-id="element-74" />
                      <div className="w-8 h-8 rounded-full bg-yellow-500/80 mix-blend-screen" data-id="element-75" />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}